import { gqlClient } from '@/api/graphql-client';
import { solarDesignDraftsKey } from '@/api/queries/project/useSolarDesignDrafts';
import type { ProjectSolarPotential } from '@/api/queries/project/useGetProjectSolarPotential';
import { graphql } from '@/gql';
import {
  newManualSolarDesignState,
  newSolarDesignState,
} from './newSolarDesignState';
import { useAccount } from '@/api/rest/users/useAccount';
import { solarDesignDraftStateSchema } from '@payaca/helpers/solarDesignDraft';
import { parseSolarPlacementRules } from '@payaca/helpers/solarPlacementRules';
import { solarSavingsRegion } from '@payaca/helpers/solarSavingsConstants';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const SolarDesignToDuplicate = graphql(`
  query SolarDesignToDuplicate($projectId: ID!, $designId: ID!) {
    projectById(id: $projectId) {
      solarDesignDraft(designId: $designId) {
        ...SolarDesignDraftFields
      }
    }
  }
`);

const CreateSolarDesignDraft = graphql(`
  mutation CreateSolarDesignDraft(
    $projectId: ID!
    $input: SaveSolarDesignDraftInput!
  ) {
    saveSolarDesignDraft(projectId: $projectId, input: $input) {
      id
      revision
    }
  }
`);

export const useCreateSolarDesign = (
  projectId: string,
  projectPublicId: string | undefined
) => {
  const queryClient = useQueryClient();
  const account = useAccount();
  const region = solarSavingsRegion(account?.region);
  const placementRules = parseSolarPlacementRules(
    account?.solarPlacementRules,
    region
  );
  return useMutation({
    mutationFn: async (input: {
      designId: string;
      name: string;
      description: string;
      /** The analysis an ANALYSIS design starts from; unused by a MANUAL one. */
      source?: ProjectSolarPotential | null;
      basis?: 'ANALYSIS' | 'MANUAL';
      duplicateId?: string;
    }) => {
      let basis = input.basis ?? 'ANALYSIS';
      let source = input.source ?? null;
      let state = source
        ? newSolarDesignState(source, { placementRules, region })
        : newManualSolarDesignState(placementRules);
      let duplicateSource: { designId: string; revision: number } | undefined;
      if (input.duplicateId) {
        const result = await gqlClient.request(SolarDesignToDuplicate, {
          projectId,
          designId: input.duplicateId,
        });
        const draft = result.projectById.solarDesignDraft;
        if (!draft || draft.isStale) {
          throw new Error(
            'This design needs its roof analysis refreshed before it can be duplicated.'
          );
        }
        basis = draft.basis;
        if (basis === 'ANALYSIS') {
          if (!draft.analysis) {
            throw new Error(
              'This design needs its roof analysis refreshed before it can be duplicated.'
            );
          }
          source = draft.analysis;
        }
        state = solarDesignDraftStateSchema.parse(draft.state);
        duplicateSource = { designId: draft.id, revision: draft.revision };
        // A new design has its own calculation identity; only Calculate can
        // attach server weather certification to it.
        delete state.weatherGeneration;
      }
      if (basis === 'ANALYSIS' && !source) {
        throw new Error(
          'An analysed design needs a roof analysis to start from.'
        );
      }
      if (!projectPublicId)
        throw new Error('The project is still loading. Try again.');
      return gqlClient.request(CreateSolarDesignDraft, {
        projectId: projectPublicId,
        input: {
          designId: input.designId,
          name: input.name.trim(),
          description: input.description.trim() || null,
          expectedRevision: 0,
          basis,
          sourceAnalysisId:
            basis === 'MANUAL' ? null : source?.sourceAnalysisId,
          sourceAnalysisVersion:
            basis === 'MANUAL' ? null : source?.sourceAnalysisVersion,
          state,
          duplicateSource,
        },
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: solarDesignDraftsKey(projectId),
      }),
  });
};
